/**
 * `SkeletonCard` — the shape of a result that has been asked for.
 *
 * Only tools with a VISIBLE result get one. Under A2UI the swap is atomic
 * without a `replaces` field: the card and the region's new `children` arrive in
 * ONE message, so the placeholder is gone and the real thing is in its place
 * within a single transaction — nothing blinks.
 *
 * `state` is where the old `status` channel went. It only ever described a
 * skeleton — the backend never shipped a non-`ready` status on a real element —
 * so it became a prop of the one component it was about. A tool that failed
 * arrives as `state: "error"` with its own hint, which is what keeps a
 * placeholder from spinning for ever.
 */

import type { SkeletonCardProps } from '../generated/schemas';
import { cn } from '../../lib/cn';

const ROWS = 3;

export default function SkeletonCard({ props }: { props: SkeletonCardProps }) {
  const failed = props.state === 'error';
  return (
    <article
      className={cn('card', 'card--skeleton', failed && 'card--skeleton-error')}
      aria-busy={!failed}
    >
      {props.hint ? <div className="skeleton__hint">{props.hint}</div> : null}
      {failed ? null : (
        <>
          <div className="skeleton__bar skeleton__bar--title" />
          {Array.from({ length: ROWS }, (_, index) => (
            <div key={index} className="skeleton__bar" style={{ width: `${92 - index * 11}%` }} />
          ))}
        </>
      )}
    </article>
  );
}
